import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, fullName, role = 'Viewer') => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName, role }
        }
      })
      if (!error && data?.user) {
        // Upsert profile in case trigger didn't fire
        await supabase?.from('user_profiles')?.upsert({
          id: data?.user?.id,
          email,
          full_name: fullName,
          role
        }, { onConflict: 'id' })
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: list all users
  const getAllUsers = async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false })
      if (error) return { data: [], error }
      return { data: data || [], error: null }
    } catch (error) {
      return { data: [], error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: invite/create a new user
  const inviteUser = async (email, fullName, role) => {
    try {
      // Check if a user profile already exists with this email
      const { data: existingProfile } = await supabase?.from('user_profiles')?.select('id')?.eq('email', email)?.maybeSingle()
      if (existingProfile) {
        return { data: null, error: { message: 'A user with this email has already been added.' }, tempPassword: null }
      }

      // Generate a temporary password
      const tempPassword = 'TempPass' + Math.random()?.toString(36)?.slice(-8) + '!'
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password: tempPassword,
        options: {
          data: { full_name: fullName, role }
        }
      })

      if (error) {
        return { data: null, error, tempPassword: null }
      }

      if (data?.user) {
        // New auth user created — upsert profile
        await supabase?.from('user_profiles')?.upsert({
          id: data?.user?.id,
          email,
          full_name: fullName,
          role
        }, { onConflict: 'id' })
        return { data, error: null, tempPassword }
      }

      // data.user is null — auth user already exists (was previously deleted from profiles only)
      // Use the SECURITY DEFINER RPC to restore/create the profile row
      const { data: restoredId, error: rpcError } = await supabase?.rpc('restore_user_profile', {
        p_email: email,
        p_full_name: fullName,
        p_role: role
      })

      if (!rpcError && restoredId) {
        return { data: { user: { id: restoredId } }, error: null, tempPassword }
      }

      // Fallback: return a clear error
      return { data: null, error: { message: 'Unable to re-add this user. The account may require manual cleanup in Supabase Auth.' }, tempPassword: null }
    } catch (error) {
      return { data: null, error: { message: 'Network error. Please try again.' }, tempPassword: null }
    }
  }

  // Admin: update user role
  const updateUserRole = async (userId, role) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ role })?.eq('id', userId)?.select()?.single()
      return { data, error }
    } catch (error) {
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: deactivate user
  const deactivateUser = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', userId)?.select()?.single()
      return { data, error }
    } catch (error) {
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: reactivate user
  const reactivateUser = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: true })?.eq('id', userId)?.select()?.single()
      return { data, error }
    } catch (error) {
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: update user details (name, role)
  const updateUserDetails = async (userId, updates) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single()
      return { data, error }
    } catch (error) {
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Admin: delete user (soft delete by removing from user_profiles)
  const deleteUser = async (userId) => {
    try {
      const { error } = await supabase?.from('user_profiles')?.delete()?.eq('id', userId)
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const isAdmin = userProfile?.role === 'Admin' || userProfile?.role === 'HR Admin'
  const isComplianceManager = userProfile?.role === 'Compliance Manager' || userProfile?.role === 'Manager'
  const isViewer = userProfile?.role === 'Viewer' || userProfile?.role === 'Employee' || userProfile?.role === 'Team Lead'

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    getAllUsers,
    inviteUser,
    updateUserRole,
    deactivateUser,
    reactivateUser,
    updateUserDetails,
    deleteUser,
    isAuthenticated: !!user,
    isAdmin,
    isComplianceManager,
    isViewer
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
